/**
 * DealCard — compact deal summary used on dashboard, review sheet, and library.
 */
import React from 'react';
import { Sparkles, User, Calendar, Tag, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const TYPE_LABEL = {
  footfall_filler: 'Footfall Filler',
  basket_builder: 'Basket Builder',
  community_event: 'Community Event',
  local_group: 'Local Group',
  waste_rescue: 'Waste Rescue',
  repeat_visit: 'Repeat Visit',
  weather_triggered: 'Weather Triggered',
  seasonal_promotion: 'Seasonal',
};

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  ai_suggested: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  scheduled: 'bg-blue-100 text-blue-800',
  live: 'bg-pink-100 text-pink-800',
  paused: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
  archived: 'bg-slate-200 text-slate-700',
};

const CONFIDENCE_STYLES = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  low: 'bg-red-50 text-red-700 border-red-200',
};

const COMPLEXITY_STYLES = {
  low: 'text-emerald-600',
  medium: 'text-amber-600',
  high: 'text-red-600',
};

export default function DealCard({ deal, onClick, actions, showFooter = true, ...rest }) {
  return (
    <Card
      onClick={onClick}
      className={`cursor-pointer hover:shadow-md transition ${onClick ? '' : 'cursor-default'}`}
      {...rest}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <Badge variant="outline" className="text-[10px]">
                {TYPE_LABEL[deal.deal_type] || deal.deal_type}
              </Badge>
              <Badge className={`text-[10px] ${STATUS_STYLES[deal.status] || ''}`}>
                {(deal.status || '').replace(/_/g, ' ')}
              </Badge>
              {deal.source === 'ai' && (
                <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI
                </Badge>
              )}
            </div>
            <div className="font-semibold leading-tight truncate">{deal.name}</div>
            {deal.goal && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{deal.goal}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {deal.target_customer && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" /> {deal.target_customer}
            </span>
          )}
          {(deal.start_date || deal.end_date) && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {deal.start_date || '—'} → {deal.end_date || '—'}
            </span>
          )}
          {deal.pos_tracking_name && (
            <span className="flex items-center gap-1">
              <Tag className="w-3 h-3" /> {deal.pos_tracking_name}
            </span>
          )}
        </div>

        {showFooter && (
          <div className="flex items-center justify-between pt-2 border-t text-xs">
            <div className="flex gap-2">
              {deal.confidence_level && (
                <span className={`px-1.5 py-0.5 rounded border ${CONFIDENCE_STYLES[deal.confidence_level] || ''}`}>
                  {deal.confidence_level} conf.
                </span>
              )}
              {deal.operational_complexity && (
                <span className={`${COMPLEXITY_STYLES[deal.operational_complexity] || ''}`}>
                  {deal.operational_complexity} complexity
                </span>
              )}
            </div>
            {deal.expected_impact && (
              <span className="flex items-center gap-1 text-emerald-600">
                <TrendingUp className="w-3 h-3" /> {deal.expected_impact}
              </span>
            )}
          </div>
        )}
        {actions && <div className="pt-2 border-t flex flex-wrap gap-2">{actions}</div>}
      </CardContent>
    </Card>
  );
}
